import React, { useEffect, useState } from 'react'
import { Card, Upload, Button, Typography, Space, Alert, Spin, Row, Col, Divider, Image, Tag } from 'antd'
import { 
  CloudUploadOutlined, 
  FileImageOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import { message } from 'antd'
import api from '../../utils/api'
import { getFileSize } from '../../utils/helpers'

const { Title, Text, Paragraph } = Typography
const { Dragger } = Upload

const UploadPage = () => {
  const [uploading, setUploading] = useState(false)
  const [ocrResult, setOcrResult] = useState(null)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [storageStatus, setStorageStatus] = useState(null)
  useEffect(() => { api.get('/api/storage/status').then(response => setStorageStatus(response.data.data)).catch(() => {}); }, [])

  const handleUpload = async (file) => {
    setUploading(true)
    setOcrResult(null)
    setUploadedFile(null)

    const formData = new FormData()
    formData.append('receipt', file)

    try {
      const response = await api.post('/api/ocr/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          console.log(`Upload progress: ${percentCompleted}%`)
        },
      })

      if (response.data.success) {
        setOcrResult(response.data.data)
        setUploadedFile({
          name: file.name,
          size: file.size,
          url: response.data.data.imageUrl
        })
        message.success('อัปโหลดและประมวลผลสำเร็จ')
      } else {
        throw new Error(response.data.message || 'อัปโหลดไม่สำเร็จ')
      }
    } catch (error) {
      console.error('Upload error:', error)
      message.error(
        error.response?.data?.message || 
        'เกิดข้อผิดพลาดในการอัปโหลด'
      )
    } finally {
      setUploading(false)
    }

    // Prevent default upload behavior
    return false
  }

  const handleReset = () => {
    setOcrResult(null)
    setUploadedFile(null)
  }

  const uploadProps = {
    name: 'receipt',
    multiple: false,
    accept: '.jpg,.jpeg,.png,.pdf',
    beforeUpload: handleUpload,
    showUploadList: false,
    disabled: uploading
  }

  return (
    <div className="upload-container fade-in">
      {/* Header */}
      <div className="form-header">
        <Title level={2} className="form-title">
          อัปโหลดเอกสาร
        </Title>
        <Paragraph className="form-subtitle">
          อัปโหลดรูปภาพใบเสร็จหรือเอกสารอื่นๆ เพื่อประมวลผลข้อมูลโดยอัตโนมัติ
        </Paragraph>
        {storageStatus && <Tag color={storageStatus.active_provider === 'google_drive' ? 'blue' : 'default'}>
          จัดเก็บที่: {storageStatus.provider_label}
        </Tag>}
      </div>

      <Row gutter={[24, 24]}>
        {/* Upload Section */}
        <Col xs={24} lg={12}>
          <Card title="อัปโหลดไฟล์" bordered={false} className="form-section">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Upload Instructions */}
              <Alert
                message="คำแนะนำการใช้งาน"
                description={
                  <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                    <li>รูปแบบไฟล์ที่รองรับ: JPG, PNG, PDF</li>
                    <li>ขนาดไฟล์สูงสุด: 10MB</li>
                    <li>ความชัดเจนของรูปภาพควรสูงเพื่อผลลัพธ์ที่ดีที่สุด</li>
                  </ul>
                }
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
              />

              {/* Upload Dragger */}
              <Dragger {...uploadProps} style={{ padding: '40px 20px' }}>
                <p className="ant-upload-drag-icon">
                  {uploading ? (
                    <Spin size="large" />
                  ) : (
                    <CloudUploadOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                  )}
                </p>
                <p className="ant-upload-text" style={{ fontSize: '18px', marginBottom: '8px' }}>
                  {uploading ? 'กำลังอัปโหลดและประมวลผล...' : 'คลิกเพื่อเลือกไฟล์ หรือลากวางที่นี่'}
                </p>
                <p className="ant-upload-hint" style={{ color: '#666', fontSize: '14px' }}>
                  อัปโหลดไฟล์รูปภาพใบเสร็จหรือเอกสารอื่นๆ เพื่อแยกข้อมูลโดยอัตโนมัติ
                </p>
              </Dragger>

              {/* Uploaded File Info */}
              {uploadedFile && (
                <Card 
                  size="small" 
                  title="ไฟล์ที่อัปโหลด"
                  extra={
                    <Button 
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />} 
                      onClick={handleReset}
                      size="small"
                    >
                      ลบ
                    </Button>
                  }
                  style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}
                >
                  <Space>
                    <FileImageOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                    <div>
                      <Text strong>{uploadedFile.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {getFileSize(uploadedFile.size)}
                      </Text>
                    </div>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                  </Space>
                </Card>
              )}
            </Space>
          </Card>
        </Col>

        {/* Results Section */}
        <Col xs={24} lg={12}>
          {ocrResult ? (
            <Card title="ผลการประมวลผล" bordered={false} className="form-section">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Image Preview */}
                {uploadedFile?.url && (
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      รูปภาพที่อัปโหลด:
                    </Text>
                    <Image
                      src={uploadedFile.url}
                      alt="Uploaded receipt"
                      width={200}
                      style={{ 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '6px',
                        maxHeight: '200px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}

                <Divider />

                {/* Extracted Data */}
                <div>
                  <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px' }}>
                    ข้อมูลที่แยกได้:
                  </Text>
                  
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Row>
                      <Col span={8}><Text strong>ผู้ขาย:</Text></Col>
                      <Col span={16}><Text>{ocrResult.ocrData?.vendor || '-'}</Text></Col>
                    </Row>
                    
                    <Row>
                      <Col span={8}><Text strong>วันที่:</Text></Col>
                      <Col span={16}><Text>{ocrResult.ocrData?.date || '-'}</Text></Col>
                    </Row>
                    
                    <Row>
                      <Col span={8}><Text strong>ยอดเงิน:</Text></Col>
                      <Col span={16}><Text>{ocrResult.ocrData?.amount || '-'} บาท</Text></Col>
                    </Row>
                    
                    <Row>
                      <Col span={8}><Text strong>ภาษี:</Text></Col>
                      <Col span={16}><Text>{ocrResult.ocrData?.tax || '-'} บาท</Text></Col>
                    </Row>
                    
                    <Row>
                      <Col span={8}><Text strong>รวมทั้งสิ้น:</Text></Col>
                      <Col span={16}>
                        <Text strong style={{ color: '#1890ff' }}>
                          {ocrResult.ocrData?.total || '-'} บาท
                        </Text>
                      </Col>
                    </Row>
                  </Space>
                </div>

                {/* Items List */}
                {ocrResult.ocrData?.items && ocrResult.ocrData.items.length > 0 && (
                  <div>
                    <Divider />
                    <Text strong style={{ display: 'block', marginBottom: '12px' }}>
                      รายการสินค้า:
                    </Text>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {ocrResult.ocrData.items.map((item, index) => (
                        <Card 
                          key={index} 
                          size="small" 
                          style={{ marginBottom: '8px', padding: '12px' }}
                        >
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>{item.description}</Text>
                            <Row>
                              <Col span={8}><Text type="secondary">จำนวน:</Text></Col>
                              <Col span={16}><Text>{item.quantity}</Text></Col>
                            </Row>
                            <Row>
                              <Col span={8}><Text type="secondary">ราคา/หน่วย:</Text></Col>
                              <Col span={16}><Text>{item.unit_price} บาท</Text></Col>
                            </Row>
                            <Row>
                              <Col span={8}><Text type="secondary">รวม:</Text></Col>
                              <Col span={16}><Text strong>{item.total} บาท</Text></Col>
                            </Row>
                          </Space>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Raw Text */}
                {ocrResult.ocrData?.raw_text && (
                  <div>
                    <Divider />
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                      ข้อความต้นฉบับ:
                    </Text>
                    <Card 
                      size="small"
                      style={{ backgroundColor: '#fafafa', padding: '12px' }}
                    >
                      <Text 
                        style={{ 
                          fontSize: '12px', 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }}
                      >
                        {ocrResult.ocrData.raw_text}
                      </Text>
                    </Card>
                  </div>
                )}

                {/* Action Buttons */}
                <Divider />
                <Space>
                  <Button type="primary" href="/receipts/new">
                    สร้างใบเสร็จ
                  </Button>
                  <Button href="/expenses/new">
                    บันทึกค่าใช้จ่าย
                  </Button>
                  <Button type="dashed" onClick={handleReset}>
                    อัปโหลดใหม่
                  </Button>
                </Space>
              </Space>
            </Card>
          ) : (
            <Card 
              title="ผลการประมวลผล" 
              bordered={false} 
              className="form-section"
              style={{ minHeight: '400px' }}
            >
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '300px',
                textAlign: 'center'
              }}>
                <ExclamationCircleOutlined 
                  style={{ 
                    fontSize: '48px', 
                    color: '#d9d9d9', 
                    marginBottom: '16px' 
                  }} 
                />
                <Text type="secondary" style={{ fontSize: '16px' }}>
                  อัปโหลดไฟล์เพื่อดูผลการประมวลผล
                </Text>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  )
}

export default UploadPage
